import React, { FC, useCallback, useEffect, useState } from 'react';

import {
	ArrowRightIcon,
	EvIcon,
	EvSelectNew,
	EvTag,
	OptionType,
	RemoveIcon,
	TAG_STATUS
} from '@evinced-private/ui-common';

import {
	ENVIRONMENT_LABEL_KEY,
	LABEL_DELIMITER,
	UNIT_ID_LABEL_KEY,
	WARNING_EMOJI
} from 'src/common/consts/platformConfigConsts';

import { buildDisplayNameToKeyMap, buildKeyToDisplayNameMap } from '../helpers';
import { TestRowSharedType } from '../helpers/types';
import { useUpdateLabelsManagement } from '../hooks/useUpdateLabelsManagement';
import { MODAL_ACTION_TITLES, MODAL_DESCRIPTIONS } from '../labels';
import { LABEL_MANAGEMENT_MODAL_TYPE } from '../shared-modal/constants';

import { UpdateTestsModalProps } from './UpdateTestsModal.types';

import './UpdateTestsModal.scss';

const ModalType = LABEL_MANAGEMENT_MODAL_TYPE.UPDATE;

export const UpdateTestsModal: FC<UpdateTestsModalProps> = ({
	testRows,
	tenantConfiguredLabels,
	configuredValues,
	onDataChange
}) => {
	const [ariaLiveState, setAriaLiveState] = useState<string>('');
	const {
		selectedLabel,
		setSelectedLabel,
		selectedNewValue,
		setSelectedNewValue,
		changes,
		labelOptions,
		valueOptions,
		removeChange
	} = useUpdateLabelsManagement<TestRowSharedType>({
		testRows,
		tenantConfiguredLabels,
		configuredValues
	});

	useEffect(() => {
		onDataChange({
			changes
		});
	}, [changes, onDataChange]);

	const handleRemoveChange = useCallback(
		(index: number, oldLabel: string) => {
			removeChange(index);
			setAriaLiveState(`${oldLabel} removed from selection`);
		},
		[removeChange]
	);

	const handleLabelChange = useCallback(
		(values: OptionType[]) => {
			setSelectedLabel(values);
		},
		[setSelectedLabel]
	);

	const handleValueChange = useCallback(
		(values: OptionType[]) => {
			setSelectedNewValue(values);
			if (values.length > 0 && selectedLabel.length > 0) {
				setAriaLiveState(`${selectedLabel[0].label} changed to ${values[0].label}`);
			}
		},
		[selectedLabel, setSelectedNewValue]
	);

	const title = `${MODAL_ACTION_TITLES[ModalType]} ${testRows.length} ${testRows.length === 1 ? 'session' : 'sessions'}`;

	const { displayNameMap, keyToDisplayNameMap } = React.useMemo(
		() => ({
			displayNameMap: buildDisplayNameToKeyMap(tenantConfiguredLabels),
			keyToDisplayNameMap: buildKeyToDisplayNameMap(tenantConfiguredLabels)
		}),
		[tenantConfiguredLabels]
	);

	return (
		<>
			<div aria-live="polite" aria-atomic="true" className="sr-only">
				{ariaLiveState}
			</div>
			<div className="update-tests-modal__content" role="region" aria-label={title}>
				<p className="update-tests-modal__description">{MODAL_DESCRIPTIONS[ModalType]}</p>

				<div className="update-tests-modal__selects-container">
					<div className="update-tests-modal__select-wrapper">
						<EvSelectNew
							id="label-select"
							label="Choose Label"
							options={labelOptions}
							selectedValue={selectedLabel}
							onSelectChange={handleLabelChange}
							placeholder="Select a label"
							listboxWidth="100%"
							shouldSortOptions={true}
						/>
					</div>

					<div className="update-tests-modal__arrow-separator">
						<EvIcon icon={ArrowRightIcon} />
					</div>

					<div className="update-tests-modal__select-wrapper">
						<EvSelectNew
							id="value-select"
							label="New Value"
							options={valueOptions}
							selectedValue={selectedNewValue}
							onSelectChange={handleValueChange}
							placeholder="Select new value"
							listboxWidth="100%"
							isDisabled={selectedLabel.length === 0}
							shouldSortOptions={true}
						/>
					</div>
				</div>

				{changes.length > 0 && (
					<>
						<div className="update-tests-modal__divider" />

						<div className="update-tests-modal__changes-section">
							<h3 className="update-tests-modal__changes-title">Labels to change</h3>
							<div className="update-tests-modal__changes-list">
								{changes.map((change, changeIndex) => {
									const oldLabelParts = change.oldLabelValue.split(LABEL_DELIMITER);
									const newLabelParts = change.newLabelValue.split(LABEL_DELIMITER);

									const oldLabelDisplay =
										oldLabelParts.length > 1
											? oldLabelParts.slice(1).join(LABEL_DELIMITER).trim()
											: '';
									const newLabelDisplay =
										newLabelParts.length > 1
											? newLabelParts.slice(1).join(LABEL_DELIMITER).trim()
											: '';
									const oldLabelKey = oldLabelParts[0].trim().toLowerCase();
									const normalizedOldKey = displayNameMap.get(oldLabelKey) || oldLabelKey;
									const oldKeyDisplayName =
										keyToDisplayNameMap.get(normalizedOldKey) || oldLabelParts[0].trim();

									// Warning logic for summary list
									let oldLabelWarning = '';

									const warningConfigs: Record<string, Set<string>> = {
										[ENVIRONMENT_LABEL_KEY.toLowerCase()]: configuredValues.environment,
										[UNIT_ID_LABEL_KEY.toLowerCase()]: configuredValues.unitId
									};

									const configuredSet = warningConfigs[normalizedOldKey];

									if (
										configuredSet?.size > 0 &&
										!Array.from(configuredSet).some(
											(v) =>
												v.localeCompare(oldLabelDisplay, undefined, { sensitivity: 'base' }) === 0
										)
									) {
										oldLabelWarning = `${WARNING_EMOJI} `;
									}

									const displayLabel = `${oldKeyDisplayName}${LABEL_DELIMITER} ${oldLabelWarning}${oldLabelDisplay}`;

									return (
										<div
											key={`${change.oldLabelValue}-${change.newLabelValue}`}
											className="update-tests-modal__change-item"
										>
											<span className="update-tests-modal__change-item-old-label">
												{displayLabel}
											</span>
											<span className="update-tests-modal__change-item-arrow">→</span>
											<EvTag
												status={TAG_STATUS.INFO}
												text={`${oldKeyDisplayName}${LABEL_DELIMITER} ${newLabelDisplay}`}
												className="update-tests-modal__change-item-tag"
											/>
											<button
												type="button"
												className="remove-icon-button update-tests-modal__change-item-remove-btn"
												onClick={() => handleRemoveChange(changeIndex, displayLabel)}
												aria-label={`Remove label change ${displayLabel} to ${oldKeyDisplayName}${LABEL_DELIMITER} ${newLabelDisplay} from selection`}
											>
												<EvIcon icon={RemoveIcon} className="remove-icon-button__x_icon" />
											</button>
										</div>
									);
								})}
							</div>
						</div>
					</>
				)}
			</div>
		</>
	);
};
